import * as D from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { Button } from './button';
import { Field, Textarea } from './form';
import { ProblemList } from './feedback';
import { isApiError } from '@/lib/api/errors';

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full';

const SIZE_CLASSES: Record<DialogSize, string> = {
  sm: 'md:max-w-md',
  md: 'md:max-w-lg',
  lg: 'md:max-w-2xl',
  xl: 'md:max-w-4xl',
  '2xl': 'md:max-w-5xl',
  '3xl': 'md:max-w-6xl',
  '4xl': 'md:max-w-7xl',
  '5xl': 'md:max-w-[1400px]',
  full: 'md:max-w-[96vw] md:h-[94vh]',
};

/** Centered dialog on larger screens, bottom sheet on phones with modern elevated styling. */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  wide,
  size,
  className,
  icon,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  size?: DialogSize;
  className?: string;
  icon?: ReactNode;
}) {
  const { t } = useTranslation();
  const resolvedSizeClass = size ? SIZE_CLASSES[size] : wide ? 'md:max-w-5xl' : 'md:max-w-lg';

  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-50 bg-fg/50 backdrop-blur-sm transition-opacity data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <D.Content
          className={cn(
            'fixed z-50 flex max-h-[94dvh] flex-col rounded-2xl border border-border/90 bg-surface shadow-2xl ring-1 ring-black/5 dark:ring-white/10 outline-none',
            'inset-x-0 bottom-0 md:inset-auto md:start-1/2 md:top-1/2 md:w-full md:-translate-y-1/2 md:ltr:-translate-x-1/2 md:rtl:translate-x-1/2',
            resolvedSizeClass,
            className,
          )}
        >
          <div className="flex items-center justify-between gap-4 border-b border-border/70 from-surface via-surface to-surface-2/40 px-6 py-4 rounded-t-2xl">
            <div className="min-w-0 flex items-center gap-3">
              {icon && (
                <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-accent-soft text-accent shadow-sm">
                  {icon}
                </div>
              )}
              <div className="min-w-0">
                <D.Title className="text-base font-bold tracking-tight text-fg truncate">{title}</D.Title>
                {description ? (
                  <D.Description className="mt-0.5 text-xs text-muted truncate">{description}</D.Description>
                ) : (
                  <D.Description className="sr-only">{title}</D.Description>
                )}
              </div>
            </div>
            <D.Close asChild>
              <Button
                variant="ghost"
                size="icon"
                className="-me-2 size-8 rounded-full text-muted hover:text-fg hover:bg-surface-2 transition-colors"
                aria-label={t('common.close')}
              >
                <X className="size-4" />
              </Button>
            </D.Close>
          </div>
          {children && <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>}
          {footer && (
            <div className="safe-bottom flex flex-col-reverse gap-2.5 border-t border-border/70 bg-surface-2/30 px-6 py-3.5 rounded-b-2xl sm:flex-row sm:justify-end">
              {footer}
            </div>
          )}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

/**
 * Confirmation for actions the server audits with a reason (>= 5 characters): reverse, cancel, redeliver, override.
 * Server rule failures (422) are listed inside the dialog so the user can act without losing the context.
 */
export function ActionDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  tone = 'primary',
  needsReason = false,
  onConfirm,
  children,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  description?: string;
  confirmLabel: string;
  tone?: 'primary' | 'danger';
  needsReason?: boolean;
  onConfirm: (reason: string) => Promise<unknown>;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    if (open) {
      setReason('');
      setError(null);
      setBusy(false);
    }
  }, [open]);
  const reasonOk = !needsReason || reason.trim().length >= 5;
  const api = isApiError(error) ? error : null;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await onConfirm(reason.trim());
      onOpenChange(false);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!busy) onOpenChange(o);
      }}
      title={title}
      description={description}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={submit}
            loading={busy}
            disabled={!reasonOk}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {(children || needsReason || error != null) && (
        <div className="grid gap-4">
          {children}
          {needsReason && (
            <Field
              label={t('common.reason')}
              hint={`${t('common.reasonHint')} (${reason.trim().length}/5)`}
              required
            >
              {(id, d) => (
                <Textarea
                  id={id}
                  aria-describedby={d}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={500}
                  autoFocus
                />
              )}
            </Field>
          )}
          {error != null &&
            (api && api.problems.length ? (
              <ProblemList problems={api.problems} title={api.message} />
            ) : (
              <p
                role="alert"
                className="rounded-md border border-danger-border bg-danger-soft px-3 py-2 text-sm text-danger"
              >
                {api ? `${t(`errors.${api.code}`)} ${api.message}` : t('errors.INTERNAL_ERROR')}
              </p>
            ))}
        </div>
      )}
    </Dialog>
  );
}
